import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { NEURO_LOGO_URL } from "@/lib/brand";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const flash = sessionStorage.getItem("nrvr_flash");
    if (flash) { sessionStorage.removeItem("nrvr_flash"); toast.error(flash); }
  }, []);

  useEffect(() => {
    if (user && user.role) {
      navigate(user.role === "admin" ? "/admin/simulations" : "/roster", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (!res.ok) {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="login-shell relative hidden lg:flex flex-col justify-between text-slate-100 p-12 overflow-hidden">
        <div className="relative z-10 flex items-center gap-3">
          <img src={NEURO_LOGO_URL} alt="NeuroRecoVR" className="h-12 w-12 rounded-xl" />
          <div>
            <div className="font-display text-lg font-bold tracking-tight">NeuroRecoVR</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-300/80">Console</div>
          </div>
        </div>

        <div className="relative z-10 space-y-6 max-w-md">
          <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-300">Clinical VR Distribution</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            Deploy. Configure. <span className="text-brand-gradient">Measure.</span>
          </h1>
          <p className="text-sm text-slate-200/80 leading-relaxed">
            A unified console for distributing neuro-rehabilitation simulations to VR headsets,
            tuning clinical parameters per patient, and observing real-world recovery telemetry.
          </p>
        </div>

        <div className="relative z-10 text-[10px] uppercase tracking-[0.22em] text-slate-300/60">
          v1.0 · Red Iron Labs
        </div>
      </div>

      {/* Right login */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src={NEURO_LOGO_URL} alt="NeuroRecoVR" className="h-10 w-10 rounded-lg" />
            <div className="font-display text-base font-bold tracking-tight">NeuroRecoVR Console</div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.22em] text-purple-700 mb-2">Sign in</div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 mb-1">Console access</h2>
          <p className="text-sm text-slate-600 mb-8">Authorized clinicians and admins only.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Your email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 h-12 rounded-full bg-white/80 border-slate-200"
                placeholder="name@domain.com"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Your password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 h-12 rounded-full bg-white/80 border-slate-200"
                placeholder="at least 8 characters"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="btn-brand w-full h-12 rounded-full font-medium tracking-wide"
            >
              {submitting ? "Signing in…" : "Login"}
            </Button>
          </form>

          <div className="mt-8 text-xs text-slate-500 border-t border-slate-200/70 pt-4">
            For login support, contact: <span className="font-mono">support@neurorecovr.com</span>
          </div>
        </div>
      </div>
    </div>
  );
}
